import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Target, Heart, Database, Zap, Package, Mail, Code2 } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";

const values = [
  { icon: Heart, key: "userCentric" },
  { icon: Database, key: "testDataManagement" },
  { icon: Zap, key: "innovation" },
  { icon: Package, key: "easyAccess" },
] as const;

const AboutUs = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="About Us – TestAssets"
        description="Learn about TestAssets, the free test data management platform built for QA teams and developers."
        path="/about-us"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "About Us", url: "/about-us" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TestAssets",
          url: "https://testassets.io",
          logo: "https://testassets.io/icon.png",
          email: "support@testassets.io",
          description: "Free test data management platform for QA teams and developers.",
          contactPoint: {
            "@type": "ContactPoint",
            email: "support@testassets.io",
            contactType: "customer support",
          },
        }}
      />
      <LandingNav />

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-16">
          {/* Hero */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-foreground">{t("aboutUs.title")}</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("aboutUs.subtitle")}
            </p>
          </div>

          {/* Story */}
          <section className="space-y-6">
            <h2 className="text-3xl font-semibold text-foreground">{t("aboutUs.ourStory.title")}</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>{t("aboutUs.ourStory.p1")}</p>
              <p>{t("aboutUs.ourStory.p2")}</p>
            </div>
          </section>

          {/* Mission */}
          <section className="space-y-6">
            <h2 className="text-3xl font-semibold text-foreground">{t("aboutUs.ourMission.title")}</h2>
            <Card>
              <CardContent className="pt-6 flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                  <Target className="h-5 w-5" />
                </div>
                <div className="space-y-3 text-muted-foreground leading-relaxed">
                  <p>{t("aboutUs.ourMission.p1")}</p>
                  <p>{t("aboutUs.ourMission.p2")}</p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Values */}
          <section className="space-y-6">
            <h2 className="text-3xl font-semibold text-foreground">{t("aboutUs.ourValues.title")}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {values.map(({ icon: Icon, key }) => (
                <Card key={key}>
                  <CardContent className="pt-6 flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        {t(`aboutUs.ourValues.${key}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t(`aboutUs.ourValues.${key}.description`)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section>
            <Card className="bg-card">
              <CardContent className="pt-6 text-center space-y-4">
                <h2 className="text-2xl font-semibold text-foreground">{t("aboutUs.cta.title")}</h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  {t("aboutUs.cta.description")}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/app">{t("aboutUs.cta.getStarted")}</Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/help">{t("aboutUs.cta.learnMore")}</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Open Source */}
          <section className="space-y-4">
            <h2 className="text-3xl font-semibold text-foreground">{t("aboutUs.openSource.title")}</h2>
            <Card>
              <CardContent className="pt-6 flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                  <Code2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-muted-foreground mb-2">
                    {t("aboutUs.openSource.description")}
                  </p>
                  <Link
                    to="/open-source-licenses"
                    className="text-primary hover:underline text-sm inline-flex items-center gap-1"
                  >
                    <Code2 className="h-3.5 w-3.5" />
                    {t("aboutUs.openSource.viewLicenses")}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Contact */}
          <section className="space-y-4">
            <h2 className="text-3xl font-semibold text-foreground">{t("aboutUs.contactUs.title")}</h2>
            <Card>
              <CardContent className="pt-6 flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-muted-foreground mb-2">{t("aboutUs.contactUs.description")}</p>
                  <a href="mailto:support@testassets.io" className="text-primary hover:underline text-sm">
                    support@testassets.io
                  </a>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
